import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface DigcompArea {
  id: string;
  brand_id: string;
  code: string;
  name: string;
  description: string | null;
  color: string;
  icon: string | null;
  sort_order: number;
  is_custom: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DigcompCompetence {
  id: string;
  brand_id: string;
  area_id: string;
  code: string;
  name: string;
  description: string | null;
  keywords: string[] | null;
  is_custom: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  area?: DigcompArea;
}

export interface DigcompLevel {
  id: string;
  brand_id: string;
  level_number: number;
  name: string;
  category: string;
  description: string | null;
  cognitive_domain: string | null;
  complexity_of_tasks: string | null;
  autonomy: string | null;
  min_score: number;
  max_score: number;
  badge_color: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StudentCompetence {
  id: string;
  brand_id: string;
  student_id: string;
  competence_id: string;
  current_level_id: string | null;
  score: number;
  assessment_type: string;
  assessed_at: string;
  evidence_sources: any[];
  notes: string | null;
  created_at: string;
  updated_at: string;
  competence?: DigcompCompetence;
  level?: DigcompLevel;
}

export interface DigcompAssessment {
  id: string;
  brand_id: string;
  student_id: string;
  assessment_date: string;
  assessment_type: string;
  status: string;
  responses: Record<string, any>;
  results: Record<string, any>;
  total_score: number | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CompetenceCertificate {
  id: string;
  brand_id: string;
  student_id: string;
  competence_id: string | null;
  area_id: string | null;
  level_id: string | null;
  certificate_type: string;
  certificate_number: string | null;
  title: string;
  description: string | null;
  issued_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

// Fetch competence areas
export function useDigcompAreas(brandId: string | null) {
  return useQuery({
    queryKey: ['digcomp-areas', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_lms_digcomp_areas')
        .select('*')
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      return data as DigcompArea[];
    },
    enabled: !!brandId,
  });
}

// Fetch competences with their areas
export function useDigcompCompetences(brandId: string | null, areaId?: string) {
  return useQuery({
    queryKey: ['digcomp-competences', brandId, areaId],
    queryFn: async () => {
      if (!brandId) return [];
      
      let query = supabase
        .from('os_lms_digcomp_competences')
        .select(`
          *,
          area:os_lms_digcomp_areas(*)
        `)
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('sort_order');
      
      if (areaId) {
        query = query.eq('area_id', areaId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as DigcompCompetence[];
    },
    enabled: !!brandId,
  });
}

// Fetch proficiency levels
export function useDigcompLevels(brandId: string | null) {
  return useQuery({
    queryKey: ['digcomp-levels', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_lms_digcomp_levels')
        .select('*')
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('level_number');
      
      if (error) throw error;
      return data as DigcompLevel[];
    },
    enabled: !!brandId,
  });
}

// Fetch student competence progress
export function useStudentCompetences(brandId: string | null, studentId?: string) {
  return useQuery({
    queryKey: ['student-competences', brandId, studentId],
    queryFn: async () => {
      if (!brandId) return [];
      
      let query = supabase
        .from('os_lms_digcomp_student_scores')
        .select(`
          *,
          competence:os_lms_digcomp_competences(*),
          level:os_lms_digcomp_levels(*)
        `)
        .eq('brand_id', brandId)
        .order('assessed_at', { ascending: false });
      
      if (studentId) {
        query = query.eq('student_id', studentId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as StudentCompetence[];
    },
    enabled: !!brandId,
  });
}

// Fetch assessments
export function useDigcompAssessments(brandId: string | null, studentId?: string) {
  return useQuery({
    queryKey: ['digcomp-assessments', brandId, studentId],
    queryFn: async () => {
      if (!brandId) return [];
      
      let query = supabase
        .from('os_lms_digcomp_assessments')
        .select('*')
        .eq('brand_id', brandId)
        .order('assessment_date', { ascending: false });
      
      if (studentId) {
        query = query.eq('student_id', studentId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as DigcompAssessment[];
    },
    enabled: !!brandId,
  });
}

// Fetch certificates
export function useCompetenceCertificates(brandId: string | null, studentId?: string) {
  return useQuery({
    queryKey: ['competence-certificates', brandId, studentId],
    queryFn: async () => {
      if (!brandId) return [];
      
      let query = supabase
        .from('os_lms_competence_certificates')
        .select('*')
        .eq('brand_id', brandId)
        .is('revoked_at', null)
        .order('issued_at', { ascending: false });
      
      if (studentId) {
        query = query.eq('student_id', studentId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as CompetenceCertificate[];
    },
    enabled: !!brandId,
  });
}

// Get aggregated stats for overview
export function useDigcompStats(brandId: string | null) {
  return useQuery({
    queryKey: ['digcomp-stats', brandId],
    queryFn: async () => {
      if (!brandId) return null;
      
      // Fetch counts in parallel
      const [areasResult, competencesResult, levelsResult, studentProgressResult, assessmentsResult, certificatesResult] = await Promise.all([
        supabase.from('os_lms_digcomp_areas').select('id', { count: 'exact' }).eq('brand_id', brandId).eq('is_active', true),
        supabase.from('os_lms_digcomp_competences').select('id', { count: 'exact' }).eq('brand_id', brandId).eq('is_active', true),
        supabase.from('os_lms_digcomp_levels').select('id', { count: 'exact' }).eq('brand_id', brandId).eq('is_active', true),
        supabase.from('os_lms_digcomp_student_scores').select('student_id', { count: 'exact' }).eq('brand_id', brandId),
        supabase.from('os_lms_digcomp_assessments').select('id', { count: 'exact' }).eq('brand_id', brandId),
        supabase.from('os_lms_competence_certificates').select('id', { count: 'exact' }).eq('brand_id', brandId).is('revoked_at', null),
      ]);
      
      // Get unique students with progress
      const { data: uniqueStudents } = await supabase
        .from('os_lms_digcomp_student_scores')
        .select('student_id')
        .eq('brand_id', brandId);
      
      const uniqueStudentCount = new Set(uniqueStudents?.map(s => s.student_id) || []).size;
      
      // Get average score
      const { data: avgScoreData } = await supabase
        .from('os_lms_digcomp_student_scores')
        .select('score')
        .eq('brand_id', brandId);
      
      const avgScore = avgScoreData && avgScoreData.length > 0
        ? avgScoreData.reduce((sum, s) => sum + (s.score || 0), 0) / avgScoreData.length
        : 0;
      
      return {
        totalAreas: areasResult.count || 0,
        totalCompetences: competencesResult.count || 0,
        totalLevels: levelsResult.count || 0,
        studentsWithProgress: uniqueStudentCount,
        totalAssessments: assessmentsResult.count || 0,
        totalCertificates: certificatesResult.count || 0,
        averageScore: Math.round(avgScore),
      };
    },
    enabled: !!brandId,
  });
}

// Seed DigComp data for a brand
export function useSeedDigcomp() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (brandId: string) => {
      const { error } = await supabase.rpc('seed_digcomp_for_brand', { p_brand_id: brandId });
      if (error) throw error;
    },
    onSuccess: (_, brandId) => {
      queryClient.invalidateQueries({ queryKey: ['digcomp-areas', brandId] });
      queryClient.invalidateQueries({ queryKey: ['digcomp-competences', brandId] });
      queryClient.invalidateQueries({ queryKey: ['digcomp-levels', brandId] });
      queryClient.invalidateQueries({ queryKey: ['digcomp-stats', brandId] });
      toast.success('DigComp 3.0 framework data seeded successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to seed data: ${error.message}`);
    },
  });
}

// Interface for linked content item
export interface DigcompLinkedContent {
  competence_id: string;
  competence_code: string;
  competence_name: string;
  area_id: string;
  area_code: string;
  area_name: string;
  area_color: string;
  events_count: number;
  episodes_count: number;
  courses_count: number;
  modules_count: number;
  lessons_count: number;
  quizzes_count: number;
  quiz_questions_count: number;
  events: Array<{
    id: string;
    title: string;
    status: string;
    target_record_date: string | null;
  }>;
  episodes: Array<{
    id: string;
    title: string;
    status: string;
    show_name: string | null;
  }>;
  courses: Array<{
    id: string;
    title: string;
  }>;
  modules: Array<{
    id: string;
    title: string;
    course_id: string;
  }>;
  lessons: Array<{
    id: string;
    title: string;
    course_id: string;
  }>;
  quizzes: Array<{
    id: string;
    title: string;
  }>;
  quiz_questions: Array<{
    id: string;
    question_text: string;
    quiz_id: string;
  }>;
}

// Fetch linked content (events and episodes) per competence
export function useDigcompLinkedContent(brandId: string | null) {
  return useQuery({
    queryKey: ['digcomp-linked-content', brandId],
    queryFn: async () => {
      if (!brandId) return [];

      // Fetch competences with areas
      const { data: competences, error: compError } = await supabase
        .from('os_lms_digcomp_competences')
        .select(`
          id,
          code,
          name,
          area_id,
          area:os_lms_digcomp_areas!inner(code, name, color)
        `)
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('code');

      if (compError) throw compError;

      // Fetch events linked to competences
      const { data: events, error: eventsError } = await supabase
        .from('os_events')
        .select('id, title, status, target_record_date, digcomp_competence_id')
        .eq('brand_id', brandId)
        .not('digcomp_competence_id', 'is', null);

      if (eventsError) throw eventsError;

      // Fetch episodes linked to competences
      const { data: episodes, error: episodesError } = await supabase
        .from('os_podcast_shows_episodes')
        .select(`
          id, 
          title, 
          status, 
          digcomp_competence_id,
          show:os_podcast_shows(name)
        `)
        .eq('brand_id', brandId)
        .not('digcomp_competence_id', 'is', null);

      if (episodesError) throw episodesError;

      // Fetch course-level competences
      const { data: courseCompetences, error: ccError } = await supabase
        .from('os_lms_course_competences')
        .select(`
          course_id,
          digcomp_competence_id,
          course:os_lms_courses!course_id(title)
        `)
        .eq('brand_id', brandId)
        .not('course_id', 'is', null)
        .is('lesson_id', null)
        .is('module_id', null);

      if (ccError) throw ccError;

      // Fetch module-level competences
      const { data: moduleCompetences, error: modError } = await supabase
        .from('os_lms_course_competences')
        .select(`
          module_id,
          digcomp_competence_id,
          module:os_lms_modules!module_id(title, course_id)
        `)
        .eq('brand_id', brandId)
        .not('module_id', 'is', null)
        .is('lesson_id', null);

      if (modError) throw modError;

      // Fetch lesson-level competences (need course_id via module for navigation)
      const { data: lessonCompetences, error: lessError } = await supabase
        .from('os_lms_course_competences')
        .select(`
          lesson_id,
          digcomp_competence_id,
          lesson:os_lms_lessons!lesson_id(title, module:os_lms_modules(course_id))
        `)
        .eq('brand_id', brandId)
        .not('lesson_id', 'is', null);

      if (lessError) throw lessError;

      // Fetch quizzes linked to competences
      const { data: quizzes, error: quizzesError } = await supabase
        .from('os_lms_quizzes')
        .select('id, title, digcomp_competence_id')
        .eq('brand_id', brandId)
        .not('digcomp_competence_id', 'is', null);

      if (quizzesError) throw quizzesError;

      // Fetch quiz questions linked to competences
      const { data: quizIds } = await supabase
        .from('os_lms_quizzes')
        .select('id')
        .eq('brand_id', brandId);
      const qIds = quizIds?.map((q) => q.id) || [];
      const { data: quizQuestions } =
        qIds.length > 0
          ? await supabase
              .from('os_lms_quiz_questions')
              .select('id, question_text, digcomp_competence_id, quiz_id')
              .in('quiz_id', qIds)
              .not('digcomp_competence_id', 'is', null)
          : { data: [] };

      // Group by competence
      const contentByCompetence: Record<string, DigcompLinkedContent> = {};

      competences?.forEach((comp: any) => {
        const area = Array.isArray(comp.area) ? comp.area[0] : comp.area;
        contentByCompetence[comp.id] = {
          competence_id: comp.id,
          competence_code: comp.code,
          competence_name: comp.name,
          area_id: comp.area_id,
          area_code: area?.code || '',
          area_name: area?.name || '',
          area_color: area?.color || '#888888',
          events_count: 0,
          episodes_count: 0,
          courses_count: 0,
          modules_count: 0,
          lessons_count: 0,
          quizzes_count: 0,
          quiz_questions_count: 0,
          events: [],
          episodes: [],
          courses: [],
          modules: [],
          lessons: [],
          quizzes: [],
          quiz_questions: [],
        };
      });

      events?.forEach((event: any) => {
        if (event.digcomp_competence_id && contentByCompetence[event.digcomp_competence_id]) {
          contentByCompetence[event.digcomp_competence_id].events_count++;
          contentByCompetence[event.digcomp_competence_id].events.push({
            id: event.id,
            title: event.title,
            status: event.status,
            target_record_date: event.target_record_date,
          });
        }
      });

      episodes?.forEach((episode: any) => {
        if (episode.digcomp_competence_id && contentByCompetence[episode.digcomp_competence_id]) {
          contentByCompetence[episode.digcomp_competence_id].episodes_count++;
          contentByCompetence[episode.digcomp_competence_id].episodes.push({
            id: episode.id,
            title: episode.title,
            status: episode.status,
            show_name: episode.show?.name || null,
          });
        }
      });

      courseCompetences?.forEach((cc: any) => {
        if (cc.digcomp_competence_id && contentByCompetence[cc.digcomp_competence_id]) {
          const course = Array.isArray(cc.course) ? cc.course[0] : cc.course;
          contentByCompetence[cc.digcomp_competence_id].courses_count++;
          contentByCompetence[cc.digcomp_competence_id].courses.push({
            id: cc.course_id,
            title: course?.title || 'Course',
          });
        }
      });

      moduleCompetences?.forEach((mc: any) => {
        if (mc.digcomp_competence_id && contentByCompetence[mc.digcomp_competence_id]) {
          const moduleData = Array.isArray(mc.module) ? mc.module[0] : mc.module;
          contentByCompetence[mc.digcomp_competence_id].modules_count++;
          contentByCompetence[mc.digcomp_competence_id].modules.push({
            id: mc.module_id,
            title: moduleData?.title || 'Module',
            course_id: moduleData?.course_id || '',
          });
        }
      });

      lessonCompetences?.forEach((lc: any) => {
        if (lc.digcomp_competence_id && contentByCompetence[lc.digcomp_competence_id]) {
          const lessonData = Array.isArray(lc.lesson) ? lc.lesson[0] : lc.lesson;
          const moduleData = lessonData?.module;
          const mod = Array.isArray(moduleData) ? moduleData[0] : moduleData;
          contentByCompetence[lc.digcomp_competence_id].lessons_count++;
          contentByCompetence[lc.digcomp_competence_id].lessons.push({
            id: lc.lesson_id,
            title: lessonData?.title || 'Lesson',
            course_id: mod?.course_id || '',
          });
        }
      });

      quizzes?.forEach((q: any) => {
        if (q.digcomp_competence_id && contentByCompetence[q.digcomp_competence_id]) {
          contentByCompetence[q.digcomp_competence_id].quizzes_count++;
          contentByCompetence[q.digcomp_competence_id].quizzes.push({
            id: q.id,
            title: q.title,
          });
        }
      });

      quizQuestions?.forEach((qq: any) => {
        if (qq.digcomp_competence_id && contentByCompetence[qq.digcomp_competence_id]) {
          contentByCompetence[qq.digcomp_competence_id].quiz_questions_count++;
          contentByCompetence[qq.digcomp_competence_id].quiz_questions.push({
            id: qq.id,
            question_text: (qq.question_text || '').slice(0, 80) + (qq.question_text?.length > 80 ? '...' : ''),
            quiz_id: qq.quiz_id,
          });
        }
      });

      // Return only competences that have linked content
      return Object.values(contentByCompetence).filter(
        c =>
          c.events_count > 0 ||
          c.episodes_count > 0 ||
          c.courses_count > 0 ||
          c.modules_count > 0 ||
          c.lessons_count > 0 ||
          c.quizzes_count > 0 ||
          c.quiz_questions_count > 0
      );
    },
    enabled: !!brandId,
  });
}

// Mapping item for holistic overview
export interface DigcompMappingItem {
  entityType: 'event' | 'podcast' | 'course' | 'module' | 'lesson' | 'quiz' | 'quiz_question';
  entityId: string;
  entityTitle: string;
  /** Used for module/lesson to navigate to course edit page */
  courseId?: string;
  competenceId: string;
  competenceCode: string;
  competenceName: string;
  areaId: string;
  levelId: string | null;
  levelName: string | null;
  updatedAt?: string;
}

// Fetch all DigComp mappings across entities for holistic view
export function useDigcompAllMappings(
  brandId: string | null,
  filters?: {
    entityType?: DigcompMappingItem['entityType'];
    competenceId?: string;
    unmappedOnly?: boolean;
  }
) {
  return useQuery({
    queryKey: ['digcomp-all-mappings', brandId, filters],
    queryFn: async () => {
      if (!brandId) return [];

      const { data: quizIdsData } = await supabase
        .from('os_lms_quizzes')
        .select('id')
        .eq('brand_id', brandId);
      const quizIds = quizIdsData?.map((q) => q.id) || [];

      const [eventsRes, episodesRes, courseCompetencesRes, quizzesRes, questionsRes] = await Promise.all([
        supabase
          .from('os_events')
          .select('id, title, updated_at, digcomp_competence_id, digcomp_area_id, digcomp_level_id')
          .eq('brand_id', brandId)
          .not('digcomp_competence_id', 'is', null),
        supabase
          .from('os_podcast_shows_episodes')
          .select('id, title, updated_at, digcomp_competence_id, digcomp_area_id, digcomp_level_id')
          .eq('brand_id', brandId)
          .not('digcomp_competence_id', 'is', null),
        supabase
          .from('os_lms_course_competences')
          .select(`
            id, course_id, module_id, lesson_id, digcomp_competence_id, digcomp_level_id, updated_at,
            competence:os_lms_digcomp_competences(code, name, area_id),
            course:os_lms_courses!course_id(title),
            module:os_lms_modules!module_id(title),
            lesson:os_lms_lessons!lesson_id(title)
          `)
          .eq('brand_id', brandId)
          .not('course_id', 'is', null),
        supabase
          .from('os_lms_quizzes')
          .select(`
            id, title, updated_at, digcomp_competence_id,
            competence:os_lms_digcomp_competences(code, name, area_id)
          `)
          .eq('brand_id', brandId)
          .not('digcomp_competence_id', 'is', null),
        quizIds.length > 0
          ? supabase
              .from('os_lms_quiz_questions')
              .select('id, question_text, updated_at, digcomp_competence_id, digcomp_area_id, digcomp_level_id')
              .in('quiz_id', quizIds)
              .not('digcomp_competence_id', 'is', null)
          : { data: [] },
      ]);

      // Fetch levels for level names
      const { data: levels } = await supabase
        .from('os_lms_digcomp_levels')
        .select('id, name')
        .eq('brand_id', brandId);
      const levelMap = new Map(levels?.map((l) => [l.id, l.name]) || []);

      const items: DigcompMappingItem[] = [];

      // Events
      eventsRes.data?.forEach((e: any) => {
        const comp = e.digcomp_competence_id;
        if (comp && (!filters?.entityType || filters.entityType === 'event') && (!filters?.competenceId || filters.competenceId === comp)) {
          items.push({
            entityType: 'event',
            entityId: e.id,
            entityTitle: e.title,
            competenceId: comp,
            competenceCode: '',
            competenceName: '',
            areaId: e.digcomp_area_id || '',
            levelId: e.digcomp_level_id,
            levelName: e.digcomp_level_id ? levelMap.get(e.digcomp_level_id) || null : null,
            updatedAt: e.updated_at,
          });
        }
      });

      // Episodes
      episodesRes.data?.forEach((e: any) => {
        const comp = e.digcomp_competence_id;
        if (comp && (!filters?.entityType || filters.entityType === 'podcast') && (!filters?.competenceId || filters.competenceId === comp)) {
          items.push({
            entityType: 'podcast',
            entityId: e.id,
            entityTitle: e.title,
            competenceId: comp,
            competenceCode: '',
            competenceName: '',
            areaId: e.digcomp_area_id || '',
            levelId: e.digcomp_level_id,
            levelName: e.digcomp_level_id ? levelMap.get(e.digcomp_level_id) || null : null,
            updatedAt: e.updated_at,
          });
        }
      });

      // Course / Module / Lesson competences
      const ccData = courseCompetencesRes.data || [];
      for (const cc of ccData) {
        const course = Array.isArray(cc.course) ? cc.course[0] : cc.course;
        const module = Array.isArray(cc.module) ? cc.module[0] : cc.module;
        const lesson = Array.isArray(cc.lesson) ? cc.lesson[0] : cc.lesson;
        const comp = Array.isArray(cc.competence) ? cc.competence[0] : cc.competence;
        const compId = cc.digcomp_competence_id;
        if (!compId || (filters?.competenceId && filters.competenceId !== compId)) continue;

        const baseItem = {
          competenceId: compId,
          competenceCode: comp?.code || '',
          competenceName: comp?.name || '',
          areaId: comp?.area_id || '',
          levelId: cc.digcomp_level_id,
          levelName: cc.digcomp_level_id ? levelMap.get(cc.digcomp_level_id) || null : null,
          updatedAt: cc.updated_at,
        };

        if (cc.lesson_id) {
          if (!filters?.entityType || filters.entityType === 'lesson') {
            items.push({
              ...baseItem,
              entityType: 'lesson',
              entityId: cc.lesson_id,
              entityTitle: lesson?.title || 'Lesson',
              courseId: cc.course_id,
            });
          }
        } else if (cc.module_id) {
          if (!filters?.entityType || filters.entityType === 'module') {
            items.push({
              ...baseItem,
              entityType: 'module',
              entityId: cc.module_id,
              entityTitle: module?.title || 'Module',
              courseId: cc.course_id,
            });
          }
        } else {
          if (!filters?.entityType || filters.entityType === 'course') {
            items.push({
              ...baseItem,
              entityType: 'course',
              entityId: cc.course_id,
              entityTitle: course?.title || 'Course',
            });
          }
        }
      }

      // Quizzes
      quizzesRes.data?.forEach((q: any) => {
        const comp = q.digcomp_competence_id;
        const compData = Array.isArray(q.competence) ? q.competence[0] : q.competence;
        if (comp && (!filters?.entityType || filters.entityType === 'quiz') && (!filters?.competenceId || filters.competenceId === comp)) {
          items.push({
            entityType: 'quiz',
            entityId: q.id,
            entityTitle: q.title,
            competenceId: comp,
            competenceCode: compData?.code || '',
            competenceName: compData?.name || '',
            areaId: compData?.area_id || '',
            levelId: null,
            levelName: null,
            updatedAt: q.updated_at,
          });
        }
      });

      // Quiz questions
      const questions = questionsRes.data || [];
      questions.forEach((q: any) => {
          if (!filters?.entityType || filters.entityType === 'quiz_question') {
            if (!filters?.competenceId || filters.competenceId === q.digcomp_competence_id) {
              items.push({
                entityType: 'quiz_question',
                entityId: q.id,
                entityTitle: (q.question_text || '').slice(0, 60) + (q.question_text?.length > 60 ? '...' : ''),
                competenceId: q.digcomp_competence_id,
                competenceCode: '',
                competenceName: '',
                areaId: q.digcomp_area_id || '',
                levelId: q.digcomp_level_id,
                levelName: q.digcomp_level_id ? levelMap.get(q.digcomp_level_id) || null : null,
                updatedAt: q.updated_at,
              });
            }
          }
        });

      // Enrich with competence names from competences table
      const { data: competences } = await supabase
        .from('os_lms_digcomp_competences')
        .select('id, code, name')
        .eq('brand_id', brandId);
      const compMap = new Map(competences?.map((c) => [c.id, c]) || []);

      items.forEach((item) => {
        const c = compMap.get(item.competenceId);
        if (c) {
          item.competenceCode = c.code;
          item.competenceName = c.name;
        }
      });

      return items;
    },
    enabled: !!brandId,
  });
}

// Fetch summary counts for linked content
export function useDigcompLinkedContentSummary(brandId: string | null) {
  return useQuery({
    queryKey: ['digcomp-linked-content-summary', brandId],
    queryFn: async () => {
      if (!brandId) return { eventsCount: 0, episodesCount: 0, coursesCount: 0, modulesCount: 0, lessonsCount: 0, quizzesCount: 0, quizQuestionsCount: 0, competencesWithContent: 0 };

      const [eventsResult, episodesResult, ccResult, modResult, lessResult, quizzesResult, quizIdsRes] = await Promise.all([
        supabase
          .from('os_events')
          .select('id, digcomp_competence_id', { count: 'exact' })
          .eq('brand_id', brandId)
          .not('digcomp_competence_id', 'is', null),
        supabase
          .from('os_podcast_shows_episodes')
          .select('id, digcomp_competence_id', { count: 'exact' })
          .eq('brand_id', brandId)
          .not('digcomp_competence_id', 'is', null),
        supabase
          .from('os_lms_course_competences')
          .select('id, digcomp_competence_id', { count: 'exact' })
          .eq('brand_id', brandId)
          .not('course_id', 'is', null)
          .is('lesson_id', null)
          .is('module_id', null),
        supabase
          .from('os_lms_course_competences')
          .select('id, digcomp_competence_id', { count: 'exact' })
          .eq('brand_id', brandId)
          .not('module_id', 'is', null)
          .is('lesson_id', null),
        supabase
          .from('os_lms_course_competences')
          .select('id, digcomp_competence_id', { count: 'exact' })
          .eq('brand_id', brandId)
          .not('lesson_id', 'is', null),
        supabase
          .from('os_lms_quizzes')
          .select('id, digcomp_competence_id', { count: 'exact' })
          .eq('brand_id', brandId)
          .not('digcomp_competence_id', 'is', null),
        supabase
          .from('os_lms_quizzes')
          .select('id')
          .eq('brand_id', brandId),
      ]);

      const quizIds = quizIdsRes.data?.map((q) => q.id) || [];
      const { data: questionsData } =
        quizIds.length > 0
          ? await supabase
              .from('os_lms_quiz_questions')
              .select('id, digcomp_competence_id')
              .in('quiz_id', quizIds)
              .not('digcomp_competence_id', 'is', null)
          : { data: [] };

      const eventCompetences = new Set(eventsResult.data?.map((e) => e.digcomp_competence_id) || []);
      const episodeCompetences = new Set(episodesResult.data?.map((e) => e.digcomp_competence_id) || []);
      const courseCompetences = new Set(ccResult.data?.map((c) => c.digcomp_competence_id) || []);
      const moduleCompetences = new Set(modResult.data?.map((c) => c.digcomp_competence_id) || []);
      const lessonCompetences = new Set(lessResult.data?.map((c) => c.digcomp_competence_id) || []);
      const quizCompetences = new Set(quizzesResult.data?.map((q) => q.digcomp_competence_id) || []);
      const questionCompetences = new Set(questionsData?.map((q) => q.digcomp_competence_id) || []);
      const allCompetences = new Set([
        ...eventCompetences,
        ...episodeCompetences,
        ...courseCompetences,
        ...moduleCompetences,
        ...lessonCompetences,
        ...quizCompetences,
        ...questionCompetences,
      ]);

      return {
        eventsCount: eventsResult.count || 0,
        episodesCount: episodesResult.count || 0,
        coursesCount: ccResult.count || 0,
        modulesCount: modResult.count || 0,
        lessonsCount: lessResult.count || 0,
        quizzesCount: quizzesResult.count || 0,
        quizQuestionsCount: questionsData?.length || 0,
        competencesWithContent: allCompetences.size,
      };
    },
    enabled: !!brandId,
  });
}
